import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { IdCard, Plus, X, Loader2, Trash2, Edit2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import { enterAdvance } from '@/hooks/useEnterAdvance'

interface Motorista {
  id: number
  nome: string
  cpf: string | null
  cnh: string | null
  categoria_cnh: string | null
  telefone: string | null
  ativo: boolean
  observacoes: string | null
}

const CATEGORIAS = ['A', 'B', 'AB', 'C', 'D', 'E', 'AC', 'AD', 'AE']

export default function ExpedicaoMotoristasPage() {
  const qc = useQueryClient()
  const [editar, setEditar] = useState<Motorista | 'new' | null>(null)

  const { data: motoristas = [], isLoading } = useQuery<Motorista[]>({
    queryKey: ['motoristas'],
    queryFn: async () => (await api.get('/expedicao/motoristas')).data,
  })

  const inativar = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/expedicao/motoristas/${id}`)).data,
    onSuccess: () => { toast.success('Motorista removido'); qc.invalidateQueries({ queryKey: ['motoristas'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao remover'),
  })

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <IdCard className="text-brand-600" /> Motoristas
          </h1>
          <p className="text-ink-600 text-sm">Cadastro de motoristas (CPF/CNH) pra carga de distribuição e MDF-e.</p>
        </div>
        <button onClick={() => setEditar('new')} className="btn-primary">
          <Plus size={14} /> Novo motorista
        </button>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th>Nome</th><th>CPF</th><th>CNH</th><th>Cat.</th><th>Telefone</th><th>Ativo</th><th className="w-24"></th></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="text-center py-8">Carregando...</td></tr>}
            {!isLoading && motoristas.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-ink-500">Nenhum motorista cadastrado.</td></tr>
            )}
            {motoristas.map(m => (
              <tr key={m.id}
                onClick={() => setEditar(m)}
                className={`hover:bg-violet-50 cursor-pointer ${m.ativo ? '' : 'opacity-50'}`}>
                <td className="font-semibold">{m.nome}</td>
                <td className="font-mono text-xs">{m.cpf || '-'}</td>
                <td className="font-mono text-xs">{m.cnh || '-'}</td>
                <td>{m.categoria_cnh || '-'}</td>
                <td className="text-xs">{m.telefone || '-'}</td>
                <td>{m.ativo ? '✓' : '—'}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <button onClick={() => setEditar(m)} className="btn-secondary !py-1 !px-2 text-xs">
                      <Edit2 size={12} />
                    </button>
                    <button onClick={() => pedirExclusao({ descricao: `o motorista ${m.nome}`, onConfirm: () => inativar.mutate(m.id) })}
                      className="btn-secondary !py-1 !px-2 text-xs text-rose-700">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editar && <MotoristaModal
        motorista={editar === 'new' ? null : editar}
        onClose={() => setEditar(null)}
        onDone={() => { setEditar(null); qc.invalidateQueries({ queryKey: ['motoristas'] }) }}
      />}
    </div>
  )
}

function MotoristaModal({ motorista, onClose, onDone }: { motorista: Motorista | null; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({
    nome: motorista?.nome || '',
    cpf: motorista?.cpf || '',
    cnh: motorista?.cnh || '',
    categoria_cnh: motorista?.categoria_cnh || 'D',
    telefone: motorista?.telefone || '',
    ativo: motorista?.ativo ?? true,
    observacoes: motorista?.observacoes || '',
    pin: '',
  })
  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }))

  const salvar = useMutation({
    mutationFn: async () => {
      if (motorista) return (await api.put(`/expedicao/motoristas/${motorista.id}`, form)).data
      return (await api.post('/expedicao/motoristas', form)).data
    },
    onSuccess: () => { toast.success(motorista ? 'Motorista atualizado' : 'Motorista criado'); onDone() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto" onKeyDown={enterAdvance}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">{motorista ? 'Editar' : 'Novo'} motorista</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <div className="text-xs font-medium mb-1">Nome *</div>
            <input className="input w-full" value={form.nome} onChange={e => set('nome', e.target.value)} placeholder="Nome do motorista" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">CPF</div>
              <input className="input w-full font-mono" value={form.cpf} onChange={e => set('cpf', e.target.value)} placeholder="000.000.000-00" />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Telefone</div>
              <input className="input w-full" value={form.telefone} onChange={e => set('telefone', e.target.value)} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">CNH</div>
              <input className="input w-full font-mono" value={form.cnh} onChange={e => set('cnh', e.target.value)} />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Categoria CNH</div>
              <select className="input w-full" value={form.categoria_cnh} onChange={e => set('categoria_cnh', e.target.value)}>
                {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <div className="text-xs font-medium mb-1">PIN do app de entregas {(motorista as any)?.tem_pin && <span className="text-emerald-600">· já definido</span>}</div>
            <input className="input w-full font-mono tracking-widest" inputMode="numeric" maxLength={8}
              value={form.pin} onChange={e => set('pin', e.target.value.replace(/\D/g, ''))}
              placeholder={(motorista as any)?.tem_pin ? '•••• (deixe em branco pra manter)' : '4 a 8 dígitos'} />
            <div className="text-[11px] text-ink-400 mt-1">O motorista entra no app com o CPF + este PIN.</div>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.ativo} onChange={e => set('ativo', e.target.checked)} />
            Ativo
          </label>
          <label className="block">
            <div className="text-xs font-medium mb-1">Observações</div>
            <textarea className="input w-full" rows={2} value={form.observacoes} onChange={e => set('observacoes', e.target.value)} />
          </label>
        </div>
        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button tabIndex={0} disabled={!form.nome || salvar.isPending} onClick={() => salvar.mutate()} className="btn-primary">
            {salvar.isPending && <Loader2 size={14} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  )
}
